import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { GenericService } from '../../core/services/generic/generic.service';
import { Injectable, inject, signal } from '@angular/core';
import { MetaPixelService } from '../../core/services/meta-pixel/meta-pixel.service';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RegistrationService {
  private generic = inject(GenericService);
  private router = inject(Router);
  private metaPixelService = inject(MetaPixelService);

  controller = 'registration';
  private schoolInfo$ = new BehaviorSubject<any>(null);
  private schoolInfoLoading = false;
  private registrationInfo$ = new BehaviorSubject<any>(null);
  private siblingInfo$ = new BehaviorSubject<any>(null);
  private currentSchoolCode: string | null = null;
  private currentStudentRegCode = null;
  private saveForm = new Subject();
  private registrationMainData: any = null;
  private stagesInfo: any = null;
  private isNextStep$ = new Subject<boolean>();
  // Emitted when a step has saved as part of a "Back" action so the shell moves
  // to the previous step instead of the next one.
  private isPrevStep$ = new Subject<boolean>();

  // Student profile picture is required by the backend to pass the Student
  // step. The preview component (which owns the dropzone) keeps this in sync;
  // the student form reads it to block early and show a toast message.
  readonly hasProfilePicture = signal(false);

  setHasProfilePicture(value: boolean): void {
    this.hasProfilePicture.set(value);
  }

  /** The active school code (route domain), e.g. "cwo" / "shb". */
  getCurrentSchoolCode(): string | null {
    return this.currentSchoolCode;
  }

  getSchoolInfo(schoolCode): Observable<any> {
    if (this.schoolInfo$.getValue() && this.currentSchoolCode === schoolCode) {
      return this.schoolInfo$.asObservable();
    } else if (!this.schoolInfoLoading) {
      return this.setSchoolInfo(schoolCode);
    } else {
      return this.schoolInfo$.asObservable();
    }
  }

  setSchoolInfo(schoolCode): Observable<any> {
    this.schoolInfoLoading = true;
    this.currentSchoolCode = schoolCode;
    return this.generic
      .getData(
        `${this.controller}/${schoolCode.toString().toLowerCase()}/school-info`,
      )
      .pipe(
        tap((response) => {
          this.schoolInfo$.next(response);
          this.schoolInfoLoading = false;

          if (response && response.data) {
            this.initializeMetaPixel(response.data);
          }
        }),
      );
  }

  private initializeMetaPixel(schoolData: any): void {
    if (schoolData.meta_pixel_enabled && schoolData.meta_pixel_id) {
      this.metaPixelService.init(schoolData.meta_pixel_id);
    }
  }

  setNextStep(value: boolean) {
    this.isNextStep$.next(value);
  }

  getNextStep(): Observable<boolean> {
    return this.isNextStep$.asObservable();
  }

  setPrevStep(value: boolean) {
    this.isPrevStep$.next(value);
  }

  getPrevStep(): Observable<boolean> {
    return this.isPrevStep$.asObservable();
  }

  getRegistrationInfo(regCode): Observable<any> {
    if (
      this.registrationInfo$.getValue() &&
      this.currentStudentRegCode === regCode
    ) {
      return this.registrationInfo$.asObservable();
    } else {
      return this.setRegistrationInfo(regCode);
    }
  }

  setRegistrationInfo(regCode): Observable<any> {
    // A different registration invalidates any document tombstones held for the
    // previous one.
    if (this.currentStudentRegCode !== regCode) {
      this.clearRemovedDocuments();
    }
    this.currentStudentRegCode = regCode;
    return this.generic
      .getData(
        `${this.controller}/${this.currentSchoolCode}/${regCode
          .toString()
          .toUpperCase()}`,
      )
      .pipe(
        tap((response) => {
          this.registrationInfo$.next(response);
        }),
      );
  }

  // Pure setter: caches an already-loaded registration payload into the
  // BehaviorSubject WITHOUT overwriting currentStudentRegCode or firing a
  // fetch. (setRegistrationInfo is the fetch-by-code method — passing a data
  // object to it corrupted currentStudentRegCode -> "[object Object]" URLs.)
  cacheRegistrationInfo(data: any): void {
    this.registrationInfo$.next(data);
  }

  clearRegistrationInfo() {
    this.registrationInfo$.next(null);
  }

  getStagesInfo() {
    return this.stagesInfo;
  }

  setStagesInfo(stages) {
    this.stagesInfo = stages;
  }

  getSaveObservable() {
    return this.saveForm.asObservable();
  }

  triggerSave(key: string, isNext = false, isLastStage, isBack = false) {
    this.saveForm.next({ key, isNext, isLastStage, isBack });
  }

  getRegistrationMainData() {
    return this.registrationMainData;
  }

  getAgeCriteriaData() {
    return this.generic.getData(
      `${this.controller}/${this.currentSchoolCode}/age-criteria`,
    );
  }

  setRegistrationMainData(data) {
    this.registrationMainData = data;
  }

  getSiblingInfo() {
    return this.siblingInfo$.asObservable();
  }

  setSiblingInfo(data) {
    this.siblingInfo$.next(data);
  }

  getAcademicYears() {
    return this.generic.getData(
      `${this.controller}/${this.currentSchoolCode}/academic-year`,
    );
  }

  getCountries() {
    return this.generic.getData(`${this.controller}/countries`);
  }

  getStates(countryId) {
    return this.generic.getData(`${this.controller}/states/${countryId}`);
  }

  getCities(stateId) {
    return this.generic.getData(`${this.controller}/cities/${stateId}`);
  }

  getGenders() {
    return this.generic.getData(`${this.controller}/genders`);
  }

  getEligibility(academicYear, dob) {
    const formattedDob = moment(dob).format('yyyy-MM-DD');
    return this.generic.getData(
      `${this.controller}/${this.currentSchoolCode}/check-eligiblity?academicYear=${academicYear}&birthDate=${formattedDob}`,
    );
  }

  getSiblingByRollNo(rollNo) {
    return this.generic.getData(
      `${this.controller}/${this.currentSchoolCode}/student-with-sibling/${rollNo}`,
    );
  }

  getParentInfoById(parentId) {
    return this.generic.getData(
      `${this.controller}/${this.currentSchoolCode}/parent-information/${parentId}`,
    );
  }

  getStagesByRegistrationCode(regCode) {
    return this.generic.getData(
      `${this.controller}/${this.currentSchoolCode}/${regCode}/stages`,
    );
  }

  getStudentByRollNo(rollNo) {
    return this.generic.getData(
      `${this.controller}/${this.currentSchoolCode}/student/${rollNo}/search-by-roll-no`,
    );
  }

  /** Dedicated roll-number verification (roll-number-worded backend messages). */
  verifyRollNo(rollNo) {
    return this.generic.getData(
      `${this.controller}/${this.currentSchoolCode}/student/${rollNo}/verify-roll-no`,
    );
  }
  uploadStudentProfilePicture(body) {
    const info = this.schoolInfo$.getValue().data;
    if (!info || !info._id) {
      throw new Error('Error while fetching school info.');
    }
    return this.generic.imageUpload(
      `${this.controller}/${info._id}/${this.currentStudentRegCode}/upload-student-profile-picture`,
      body,
    );
  }

  saveStudentProfilePicture(url) {
    const body = {
      profilePicture: url,
    };
    return this.generic.patch(
      `${this.controller}/${this.currentSchoolCode}/${this.currentStudentRegCode}/student-profile-picture`,
      body,
    );
  }

  uploadDocument(body) {
    const info = this.schoolInfo$.getValue().data;
    if (!info || !info._id) {
      throw new Error('Error while fetching school info.');
    }
    return this.generic.upload(
      `${this.controller}/${info._id}/${this.currentStudentRegCode}/upload-document`,
      body,
    );
  }

  removeDocument(title, documentUrl) {
    return this.generic.deleteWithOutId(
      `${this.controller}/${this.currentSchoolCode}/${this.currentStudentRegCode}/remove-document?documentUrl=${documentUrl}`,
    );
  }

  // Client-side tombstones for documents the user has deleted. The backend's
  // registration payload can still list a just-removed document, which made the
  // Documents step re-bind it (showing "Download" again) after navigating away
  // and back. The Documents step marks a title removed on a successful delete,
  // filters tombstoned titles out when rebuilding the form, and clears the
  // tombstone when a fresh file is uploaded to that slot.
  private removedDocumentTitles = new Set<string>();

  markDocumentRemoved(title: string): void {
    if (title) {
      this.removedDocumentTitles.add(title);
    }
  }

  unmarkDocumentRemoved(title: string): void {
    this.removedDocumentTitles.delete(title);
  }

  isDocumentRemoved(title: string): boolean {
    return this.removedDocumentTitles.has(title);
  }

  clearRemovedDocuments(): void {
    this.removedDocumentTitles.clear();
  }

  createStudentRegistration(formData) {
    const body = {
      academicYear: formData.academicYear,
      branch: formData.branch,
      grade: formData.grade,
      schoolCode: this.currentSchoolCode,
      regEmail: formData.contactEmail,
      regContactNumber: formData.phoneNumber,
      regContactCountryCode: formData.phoneCountryCode,
      studentInformation: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        birthDate: moment(formData.dob).format('yyyy-MM-DD'),
      },
    };

    if (formData.referralCode && formData.referralCode !== '') {
      body['referralCode'] = formData.referralCode;
    }

    return this.generic.create(`${this.controller}`, body);
  }

  createStudentInfo(formData) {
    const body: any = {
      gender: formData.gender,
      nationality: formData.nationality,
      address: {
        address: formData.address,
        country: formData.country,
        state: formData.state,
        city: formData.city,
      },
    };
    if (formData.birthPlace) {
      body.birthPlace = formData.birthPlace;
    }
    if (formData.postalCode) {
      body.address.postalCode = formData.postalCode;
    }
    if (formData.emailAddress) {
      body.email = formData.emailAddress;
    }
    if (formData.phoneCountryCode) {
      body.countryCode = formData.phoneCountryCode;
    }
    if (formData.phoneNumber) {
      body.phoneNumber = formData.phoneNumber;
    }
    // Roll-number fields (collected from every applicant).
    if (formData.hasRollNo !== null && formData.hasRollNo !== undefined) {
      body.hasRollNo = formData.hasRollNo;
    }
    if (formData.cnic) {
      body.cnic = formData.cnic;
    }
    if (formData.rollNo) {
      body.rollNo = formData.rollNo;
    }

    return this.generic.create(
      `${this.controller}/${this.currentSchoolCode}/${this.currentStudentRegCode}/student-information`,
      body,
    );
  }

  createSiblingInfo(body) {
    return this.generic.create(
      `${this.controller}/${this.currentSchoolCode}/${this.currentStudentRegCode}/sibling-information`,
      body,
    );
  }

  createParentInfo(formValue) {
    const requestBody = {
      fatherInformation: this.cleanObject({
        name: formValue.fatherInformation.name,
        email: formValue.fatherInformation.email,
        cnic: formValue.fatherInformation.cnic,
        occupation: formValue.fatherInformation.occupation,
        countryCode: formValue.fatherInformation.countryCode,
        cell: formValue.fatherInformation.cell,
        designation: formValue.fatherInformation.designation,
        company: formValue.fatherInformation.company,
        businessType: formValue.fatherInformation.businessType,
        highestQualification: formValue.fatherInformation.highestQualification,
        recentInstituteAttended:
          formValue.fatherInformation.recentInstituteAttended,
      }),
      motherInformation: this.cleanObject({
        name: formValue.motherInformation.name,
        email: formValue.motherInformation.email,
        cnic: formValue.motherInformation.cnic,
        occupation: formValue.motherInformation.occupation,
        countryCode: formValue.motherInformation.countryCode,
        cell: formValue.motherInformation.cell,
        designation: formValue.motherInformation.designation,
        company: formValue.motherInformation.company,
        businessType: formValue.motherInformation.businessType,
        highestQualification: formValue.motherInformation.highestQualification,
        recentInstituteAttended:
          formValue.motherInformation.recentInstituteAttended,
      }),
      emergencyContact: this.cleanObject({
        name: formValue.emergencyContact.name,
        countryCode: formValue.emergencyContact.countryCode,
        cell: formValue.emergencyContact.cell,
      }),
    };
    return this.generic.create(
      `${this.controller}/${this.currentSchoolCode}/${this.currentStudentRegCode}/parent-information`,
      requestBody,
    );
  }

  cleanObject(obj: any): any {
    const cleanedObj = { ...obj };
    Object.keys(cleanedObj).forEach((key) => {
      if (
        cleanedObj[key] === null ||
        cleanedObj[key] === undefined ||
        cleanedObj[key] === ''
      ) {
        delete cleanedObj[key];
      }
    });
    return cleanedObj;
  }

  createGeneralInfo(body) {
    return this.generic.create(
      `${this.controller}/${this.currentSchoolCode}/${this.currentStudentRegCode}/general-information`,
      body,
    );
  }

  createTermsAndConditions(body) {
    return this.generic.create(
      `${this.controller}/${this.currentSchoolCode}/${this.currentStudentRegCode}/terms-and-conditions`,
      body,
    );
  }

  createDocument() {
    const body = {};
    return this.generic.create(
      `${this.controller}/${this.currentSchoolCode}/${this.currentStudentRegCode}/documents`,
      body,
    );
  }

  completeRegistration() {
    return this.generic.create(
      `${this.controller}/${this.currentSchoolCode}/${this.currentStudentRegCode}/complete`,
      {},
    );
  }

  navigateToFinish() {
    this.router.navigateByUrl(
      '/' +
        this.currentSchoolCode +
        '/admission/' +
        this.currentStudentRegCode +
        '/finish',
    );
  }
}
